import {
   SurveyCreator,
   SurveyCreatorComponent,
   localization,
   settings,
} from 'survey-creator-react';
import 'survey-core/survey.i18n.js';
import 'survey-creator-core/survey-creator-core.i18n.js';
import 'survey-core/defaultV2.css';
import 'survey-creator-core/survey-creator-core.css';
import { propertyGridWhitelist } from './propertyGridWhitelist';
import { setLicenseKey, ItemValue, Serializer, Action, Model, NumericValidator } from 'survey-core';
import { useLocalization } from '~/framework';
import { useSurveyJsConnection } from './useSurveyJsConnection';
import {
   Button,
   Center,
   CircularProgress,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   Stack,
   ModalCloseButton,
   Text,
   useToast,
   useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { isEqual } from 'lodash';
import { useSiteInfo } from '~/pages/_shared';
import { Guide, WikiObject } from '~/monolith-api';
import styles from './quizEditorStyles.module.css';
import {
   CUSTOM_IMAGE_TYPE,
   CUSTOM_VIDEO_TYPE,
   registerImageUploadQuestion,
   registerVideoUploadQuestion,
} from './CustomQuestionTypes';
import { MediaLibrary } from '~/ui-components';

type PropertyChangedEvent = { name: string };

type SurveyCreatorPageProps = {
   incomingQuizJson: string;
   saveQuizFunc: (
      quizJson: string,
      returnToDoc: boolean,
      setIsModified: (value: boolean) => void
   ) => void;
   docUrl: string;
   doc?: Guide | WikiObject;
   isEnabled: boolean;
};

type ImagePickerChoice = {
   value: number;
   imageLink?: string;
};

registerImageUploadQuestion();
registerVideoUploadQuestion();

export const SurveyCreatorPage = ({
   incomingQuizJson,
   saveQuizFunc,
   docUrl,
   doc,
   isEnabled,
}: SurveyCreatorPageProps) => {
   const { _js, dateFnsLocale, translationSettings } = useLocalization();
   const { data: connection } = useSurveyJsConnection();
   const toast = useToast();
   const { isOpen, onOpen, onClose } = useDisclosure();
   const [cleanQuizJson, setQuizJson] = useState(() => JSON.parse(incomingQuizJson));
   const [isModified, setIsModified] = useState(false);
   const timer = useRef<NodeJS.Timeout>();
   const router = useRouter();
   const { data: siteInfo } = useSiteInfo();
   settings.designer.defaultAddQuestionType = 'radiogroup';
   const {
      isOpen: isMediaLibraryOpen,
      onOpen: onOpenMediaLibrary,
      onClose: onCloseMediaLibrary,
   } = useDisclosure();
   const [openFileChooserOptions, setOpenFileChooserOptions] = useState<any>();

   useEffect(() => {
      const handleWindowClose = (e: Event) => {
         if (!isModified) {
            return;
         }
         e.preventDefault();
      };

      window.addEventListener('beforeunload', handleWindowClose);

      return () => {
         window.removeEventListener('beforeunload', handleWindowClose);
      };
   }, [isModified]);

   const creatorSettings = () => {
      const options = {
         showLogicTab: false,
         showJSONEditorTab: false,
         showObjectTitles: true,
         showSidebar: false, // Hides the Property Grid when first loading
         questionTypes: ['boolean', 'text', 'radiogroup', CUSTOM_IMAGE_TYPE, CUSTOM_VIDEO_TYPE],
         sendResultOnPageNext: true, // Saves partial responses
         questionsOnPageMode: 'singlePage', // Displays all questions on one page to quiz taker
         allowModifyPages: false, // Only allows one page for quiz creation
      };

      if (!siteInfo?.live_site) {
         options.questionTypes.push('imagepicker');
      }

      const theme = {
         cssVariables: {
            '--sjs-primary-backcolor': '#3b82f6',
            '--sjs-primary-backcolor-light': 'rgba(59, 130, 246, 0.1)',
            '--sjs-primary-backcolor-dark': 'rgba(55, 122, 231, 1)',
         },
      };
      const creator = new SurveyCreator(options);
      creator.theme = theme;
      creator.pageEditMode = 'single';
      // Set the survey creator locale to translate SurveyJS strings, default to English if not set
      const locale = dateFnsLocale ? dateFnsLocale.code : 'en';
      creator.locale = locale ?? 'en';

      // Update boolean display
      Serializer.getProperty('boolean', 'renderAs').defaultValue = 'radio';
      // Hide Survey description and logo button
      Serializer.getProperty('survey', 'description').visible = false;
      Serializer.getProperty('survey', 'logo').visible = false;

      Serializer.addProperty('question', {
         default: 0,
         type: 'number',
         visibleIndex: 0,
         category: 'data',
         name: 'numberTolerance',
         displayName: _js('Tolerance'),
         onSetValue: (survey: any, value: string | number) =>
            survey.setPropertyValue('numberTolerance', value === '' ? 0 : value),
      });

      if (siteInfo?.['feature-quiz-imports']) {
         Serializer.addProperty('survey', {
            default: isEnabled,
            type: 'radiogroup',
            choices: [
               {
                  value: true,
                  text: _js('Active'),
               },
               {
                  value: false,
                  text: _js('Inactive'),
               },
            ],
            category: 'general',
            name: 'quizEnabledStatus',
            displayName: _js('Quiz Active Status'),
         });
      }

      creator.toolbox.defineCategories([
         {
            category: 'general',
            items: ['boolean', 'text', 'radiogroup'],
         },
         {
            category: 'media-questions',
            items: [CUSTOM_IMAGE_TYPE, CUSTOM_VIDEO_TYPE, 'imagepicker'],
         },
      ]);

      //Text labels
      const labels = localization.getLocale('');
      if (translationSettings.locale != 'en') {
         labels.pe.isRequired = '';
         labels.pe.delete = '';
         labels.ed.settings = '';
      }

      labels.ed.survey = _js('Quiz');
      labels.ed.surveySettings = _js('Quiz Settings');
      labels.ed.surveySettingsToolTip = _js('Open Quiz Settings');
      labels.ed.surveyTypeName = _js('Quiz');
      labels.ed.testSurveyAgain = _js('Preview Quiz Again');
      labels.ed.testSurveyWidth = _js('Quiz width: ');
      labels.ed.embedSurvey = _js('Embed Quiz labels.');
      labels.ed.saveSurvey = _js('Save Quiz');
      labels.ed.saveSurveyTooltip = _js('Save Quiz');
      labels.ed.surveyResults = _js('Quiz Results');
      labels.ed.surveyPlaceholder = _js(
         'The quiz is empty. Drag an element from the toolbox on the left or click the button below to add a question.'
      );
      labels.ew.showOnPage = _js('Show quiz on a page');
      labels.ew.showInWindow = _js('Show quiz in a window');
      labels.ew.loadFromServer = _js('Load Quiz JSON from server');
      labels.p.surveyId = _js('Quiz id');
      labels.p.surveyPostId = _js('Quiz post id');
      labels.p.surveyShowDataSaving = _js('Quiz show data saving');
      labels.pe.surveyTitlePlaceholder = _js('Quiz Title');
      labels.pe.sendResultOnPageNext = _js('Save partial quiz results in progress');
      labels.pe.completeText = _js('Complete Quiz button text');
      labels.pe.showCompletedPage = _js('Show the Quiz Complete page');
      labels.pe.showPreviewBeforeComplete = _js('Preview answers before submitting the quiz');
      labels.pe.surveyEditorTitle = _js('Edit quiz settings');
      labels.pe.includeIntoResult = _js('Include into quiz results');
      labels.pe.locale = _js('Quiz language');
      labels.pe.name = _js('Unique Identifier');
      labels.pe.startSurveyText = _js('Start Quiz button text');
      labels.pe.questionsOnPageMode = _js('Quiz structure');
      labels.pe.completedBeforeHtml = _js('Markup to show if the user already completed this quiz');
      labels.pe.completedHtml = _js('Quiz Complete page markup');
      labels.pe.completedHtmlOnCondition = _js('Dynamic Quiz Complete page markup');
      labels.pe.loadingHtml = _js('Markup to show while quiz model is loading');
      labels.pe.noquestions = _js('There are no questions in the quiz.');
      labels.pe.tabs.data = _js('Answer Data');
      labels.pe.tabs.showOnCompleted = _js('Quiz Complete');
      labels.pe.tabs.logo = _js('Logo in Quiz Header');
      labels.pe.triggerCompleteText = _js('Complete the quiz if succeed.');
      labels.pv.onSurvey = _js('Continue throughout the quiz');
      labels.pv.onComplete = _js('When the quiz is completed');
      labels.pv.survey = _js('Quiz');
      labels.pehelp.quizEnabledStatus = _js(
         'Inactive quizzes are hidden from non-privileged users and unavailable for assignment.'
      );
      labels.qt[CUSTOM_IMAGE_TYPE] = _js('Image');
      labels.pehelp[CUSTOM_IMAGE_TYPE] = _js('Upload image to display within the quiz');
      labels.qt[CUSTOM_VIDEO_TYPE] = _js('Video');

      // Hide all other properties for all question types and the survey settings
      creator.onShowingProperty.add((_, options) => {
         const type = options.obj.getType();
         const propName = options.property.name;
         if (type === 'radiogroup') {
            options.canShow = propertyGridWhitelist.radioPropsWhitelist.indexOf(propName) > -1;
         }
         if (type === 'boolean') {
            options.canShow = propertyGridWhitelist.boolPropsWhitelist.indexOf(propName) > -1;
         }
         if (type === 'text') {
            options.canShow = propertyGridWhitelist.textPropsWhitelist.indexOf(propName) > -1;
         }
         if (type === 'survey') {
            options.canShow = propertyGridWhitelist.surveyPropsWhitelist.indexOf(propName) > -1;
         }
         if (type === 'page') {
            options.canShow = propertyGridWhitelist.pagePropsWhitelist.indexOf(propName) > -1;
         }
         if (type === 'itemvalue') {
            options.canShow = propertyGridWhitelist.itemValuePropsWhitelist.indexOf(propName) > -1;
         }
         if (type === CUSTOM_IMAGE_TYPE || type === CUSTOM_VIDEO_TYPE) {
            options.canShow = propertyGridWhitelist.imagePropsWhitelist.indexOf(propName) > -1;
         }
         if (type === 'imagepicker') {
            options.canShow =
               propertyGridWhitelist.imagePickerPropsWhitelist.indexOf(propName) > -1;
         }
      });
      creator.JSON = cleanQuizJson;

      //Validators and settings
      creator.onQuestionAdded.add(function (_, options) {
         const question = options.question;

         question.onPropertyChanged.add(function (question, options) {
            settings.designer.defaultAddQuestionType = question.getType();
         });

         if (question.getType() === 'text') {
            let validator = new NumericValidator();
            validator.text = 'Value must be a number.';
            question.validators.push(validator);
         }
         if (question.getType() === 'boolean') {
            question.renderAs = 'radio';
         }
      });

      // Remove the input type adorner to prevent changing from number input
      creator.onElementAllowOperations.add((_, options) => {
         if (options.obj.getType() === 'text') {
            // Prevents user from using a different input type, only allows number input
            options.allowChangeInputType = false;
         } else if (
            options.obj.getType() === CUSTOM_IMAGE_TYPE ||
            options.obj.getType() === CUSTOM_VIDEO_TYPE
         ) {
            options.allowChangeRequired = false;
         }
         options.allowEdit = true; // Displays the "settings" adorner
         options.allowCopy = false; // Hides "duplicate" adorner
      });

      // Update the correct answer input to prevent invalid values
      creator.onPropertyGridShowModal.add((_, options) => {
         if (options.obj.getType() === 'text') {
            const answerModal = document.getElementsByClassName('sv-popup--modal')[0];
            const answerInput = answerModal.getElementsByTagName('input')[0];
            answerInput.type = 'number';
         }
      });

      // By default SurveyJS uses images of animals, override to return no default image instead
      const imagepickerToolboxItem = creator.toolbox.getItemByName('imagepicker');
      if (imagepickerToolboxItem) {
         imagepickerToolboxItem.json.choices = [
            { value: 1, imageLink: '' },
            { value: 2, imageLink: '' },
            { value: 3, imageLink: '' },
            { value: 4, imageLink: '' },
         ];
      }

      const debouncedModifyHandler = () => {
         if (timer.current) {
            clearTimeout(timer.current);
         }
         timer.current = setTimeout(() => {
            setIsModified(compareQuizJson());
         }, 100);
         return;
      };

      creator.onModified.add(debouncedModifyHandler);
      creator.onModified.add((e, options) => {
         const question = options.target;
         const questionConverted = 'QUESTION_CONVERTED';
         const correctAnswer = question?.getPropertyValue('correctAnswer');
         if (options.type === questionConverted) {
            options.newValue.correctAnswer = null;
         }
         if (question?.getType() === 'imagepicker' && options.oldValue) {
            if (correctAnswer == options.oldValue[0]?.id && options.newValue?.length < 1) {
               question.setPropertyValue('correctAnswer', null);
            }
         }
         if (!question || question.getType() !== 'radiogroup') return;
         const choices = question.getPropertyValue('choices') || [];
         const isOtherEnabled = question.getPropertyValue('showOtherItem');
         const isNoneEnabled = question.getPropertyValue('showNoneItem');
         const isStandardChoice = choices.some(
            (choice: ItemValue) => choice.value === correctAnswer
         );
         const isAnswerNone = correctAnswer === 'none';
         const isAnswerOther = !isStandardChoice && !isAnswerNone;
         const propertyChanged = (options as Partial<PropertyChangedEvent>).name;
         if (
            (propertyChanged === 'choices' && !isStandardChoice) ||
            (propertyChanged === 'showNoneItem' && !isNoneEnabled && isAnswerNone) ||
            (propertyChanged === 'showOtherItem' && !isOtherEnabled && isAnswerOther)
         ) {
            question.setPropertyValue('correctAnswer', null);
         }
      });

      const createCorrectAnswerAdorner = () => {
         return new Action({
            id: 'correctAnswer',
            title: translationSettings.locale == 'en' ? _js('Correct Answer') : '',
            tooltip: _js('Add correct answer value'),
            iconName: 'icon-v2check_24x24',
            iconSize: 16,
            action: () => {
               creator.collapseAllPropertyGridCategories();
               creator.expandPropertyGridCategory('data');
               creator.showSidebar = true;
            },
            visibleIndex: 10, // Replaces duplicate adorner position
            css: 'sv-action-bar-item--secondary',
         });
      };

      //Add a 'correct answer' button shortcut
      creator.onDefineElementMenuItems.add((_, options) => {
         if (
            options.obj.getType() !== 'page' &&
            options.obj.getType() !== CUSTOM_IMAGE_TYPE &&
            options.obj.getType() !== CUSTOM_VIDEO_TYPE
         ) {
            const correctAnswerAdorner = createCorrectAnswerAdorner();
            options.items.push(correctAnswerAdorner);

            if (translationSettings.locale != 'en') {
               for (const item of options.items) {
                  if (item.id === 'delete') {
                     item.tooltip = _js('Delete');
                  }
                  if (item.id === 'isrequired') {
                     item.tooltip = _js('Required');
                  }
               }
            }
         }
      });

      //Create the bottom Quiz Buttons.
      creator.toolbar.actions.unshift(
         new Action({
            id: 'quiz-save',
            title: _js('Save'),
            tooltip: _js('Save changes'),
            action: () => {
               if (compareQuizJson()) {
                  saveQuiz(false);
               } else {
                  return;
               }
            },
         })
      );

      creator.toolbar.actions.unshift(
         new Action({
            id: 'quiz-save-and-exit',
            title: _js('Save & Exit'),
            tooltip: _js('Save changes and exit quiz editor'),
            action: () => {
               if (compareQuizJson()) {
                  saveQuiz(true);
               } else {
                  router.push(docUrl);
               }
            },
         })
      );

      creator.toolbar.actions.unshift(
         new Action({
            id: 'quiz-exit',
            title: _js('Exit Editor'),
            tooltip: _js('Discard unsaved changes and exit quiz editor'),
            action: () => {
               if (compareQuizJson()) {
                  onOpen();
               } else {
                  router.push(docUrl);
               }
            },
         })
      );

      if (siteInfo?.['feature-quiz-imports']) {
         creator.toolbar.actions.unshift(
            new Action({
               id: 'quiz-active-status',
               iconName: 'icon-modernradio',
               innerCss: isEnabled ? styles.activeLabel : styles.inactiveLabel,
               title: isEnabled ? _js('Active') : _js('Inactive'),
               tooltip: _js('Active status of quiz'),
               action: () => {
                  const survey = surveyModel().getSurvey();
                  creator.selectElement(survey);
                  creator.focusElement(survey, true, survey, 'quizEnabledStatus', true);
                  creator.showSidebar = true;
               },
            })
         );
      }

      if (!creator.JSON.title && doc) {
         creator.survey.title = _js('%1: Quiz', doc.title);
      }

      // Customize toolbox items
      const textToolboxItem = creator.toolbox.getItemByName('text');
      // Update title and tooltip text, default is 'Single Line Input'
      textToolboxItem.title = _js('Number Input');
      textToolboxItem.tooltip = _js('Number Input');
      // Update title and tooltip text, default is 'Radio Button Group'
      creator.toolbox.getItemByName('radiogroup').title = _js('Multiple Choice');
      creator.toolbox.getItemByName('radiogroup').tooltip = _js('Multiple Choice');
      // Update title and tooltip text, default is 'Yes/No (Boolean)'
      creator.toolbox.getItemByName('boolean').title = _js('True/False');
      creator.toolbox.getItemByName('boolean').tooltip = _js('True/False');
      // Update title and tooltip text, default is 'image-upload'
      creator.toolbox.getItemByName(CUSTOM_IMAGE_TYPE).title = _js('Image');
      creator.toolbox.getItemByName(CUSTOM_IMAGE_TYPE).tooltip = _js('Image');
      // Update title and tooltip text, default is 'video-upload'
      creator.toolbox.getItemByName(CUSTOM_VIDEO_TYPE).title = _js('Video');
      creator.toolbox.getItemByName(CUSTOM_VIDEO_TYPE).tooltip = _js('Video');

      return creator;
   };
   const [creator, setCreator] = useState(creatorSettings());

   useEffect(() => {
      const handleOpenFileChooser = (_: any, options: any) => {
         setOpenFileChooserOptions(options);
         onOpenMediaLibrary();
      };

      const handleUploadFile = (_: any, options: any) => {
         options.files.forEach((file: { webkitRelativePath: string }) => {
            options.callback('success', file.webkitRelativePath);
         });
      };

      creator.onOpenFileChooser.add(handleOpenFileChooser);
      creator.onUploadFile.add(handleUploadFile);

      return () => {
         creator.onOpenFileChooser.remove(handleOpenFileChooser);
         creator.onUploadFile.remove(handleUploadFile);
      };
   }, [creator, setOpenFileChooserOptions, onOpenMediaLibrary]);

   const surveyModel = () => new Model(creator.JSON);

   const hasDuplicateImage = (imageChoices: ImagePickerChoice[]) => {
      const seen = new Set();
      for (const image of imageChoices) {
         if (seen.has(image.imageLink)) {
            return true;
         }
         seen.add(image.imageLink);
      }
      return false;
   };

   const validateQuizJson = () => {
      let isValid = true;
      let errorMsg = null;
      if (!creator.JSON.title) {
         isValid = false;
         errorMsg = _js('Please add a title before saving');
         return { isValid, errorMsg };
      }
      const validatedJson = creator.JSON;
      if (validatedJson.elements) {
         for (const question of validatedJson.elements) {
            if (question.type === CUSTOM_IMAGE_TYPE || question.type === CUSTOM_VIDEO_TYPE) {
               if (!question.hasOwnProperty('imageId') || !question.hasOwnProperty('mediaType')) {
                  const type = question.type === CUSTOM_IMAGE_TYPE ? 'an image' : 'a video';
                  isValid = false;
                  errorMsg = _js(
                     'The media uploader is empty, please upload %1 to "%2"',
                     type,
                     question.name
                  );
                  break;
               }
               // There is no other validation needed on these question types
               continue;
            }
            if (question.type === 'imagepicker') {
               question.choices = question.choices.filter((choice: ImagePickerChoice) => {
                  return choice.hasOwnProperty('imageLink') && choice.imageLink !== '';
               });
               if (question.choices.length < 2) {
                  isValid = false;
                  errorMsg = _js(
                     'At least two options must be uploaded to Image Picker Questions. Please upload images to "%1"',
                     question.name
                  );
                  break;
               }
               if (hasDuplicateImage(question.choices)) {
                  isValid = false;
                  errorMsg = _js(
                     'There are duplicate images in question "%1" please remove duplicates.',
                     question.name
                  );
                  break;
               }
            }
            if (!question.hasOwnProperty('correctAnswer')) {
               isValid = false;
               errorMsg = _js(
                  'Each question must have an answer value. Please add a correct answer to "%1" within the question settings.',
                  question.name
               );
               break;
            }
            if (question.type === 'text') {
               //Also check to see to see if the "correctAnswer" is a number.
               const questionEl = surveyModel().getQuestionByName(question.name);
               if (isNaN(parseFloat(questionEl.correctAnswer))) {
                  isValid = false;
                  errorMsg = _js(
                     '"Number Input" questions must have a valid number as an answer value. Please add a correct answer to "%1" within the question settings.',
                     question.name
                  );
                  break;
               }
            }
            if (question.type === 'radiogroup') {
               // If the answer is not in the choices array, and is not none, it is the other option
               question.answerIsOther =
                  question.choices.indexOf(question.correctAnswer) < 0 &&
                  question.correctAnswer !== 'none';
            }
         }
      } else {
         isValid = false;
         errorMsg = _js('Each quiz must have at least one question');
         return { isValid, errorMsg };
      }
      return { isValid, errorMsg, validatedJson };
   };

   const saveQuiz = (returnToDoc: boolean) => {
      setQuizJson(creator.JSON);
      toast({
         title: _js('Saving. . . '),
         position: 'top',
         status: 'info',
         duration: 5000,
         isClosable: true,
      });
      const { isValid, errorMsg, validatedJson } = validateQuizJson();
      if (errorMsg) {
         toast.closeAll();
         toast({
            title: errorMsg,
            position: 'top',
            status: 'error',
            duration: 2000,
            isClosable: true,
         });
      }
      if (isValid) {
         saveQuizFunc(JSON.stringify(validatedJson), returnToDoc, setIsModified);
      }
   };

   /**
    * Returns true if the current quiz JSON is different from the initial quiz JSON
    * @returns boolean
    */
   const compareQuizJson = () => {
      const currentJson = creator.JSON;
      const currentQuestions = currentJson.elements;
      const initialQuestions = cleanQuizJson.elements || cleanQuizJson.pages[0].elements;
      if (cleanQuizJson.title !== currentJson.title) {
         return true;
      }
      if (cleanQuizJson.questionsOrder !== currentJson.questionsOrder) {
         return true;
      }
      if (!currentQuestions || initialQuestions.length !== currentQuestions.length) {
         return true;
      }
      for (let i = 0; i < initialQuestions.length; i++) {
         const { renderAs, answerIsOther, ...initialQuestion } = initialQuestions[i];
         const currentQuestion = currentQuestions[i];
         if (!isEqual(initialQuestion, currentQuestion)) {
            return true;
         }
      }
      return false;
   };

   if (connection) {
      setLicenseKey(connection.licenseKey);
      return (
         <Stack height="100vh" display="block">
            <SurveyCreatorComponent creator={creator} />
            <Modal isOpen={isOpen} onClose={onClose}>
               <ModalOverlay />
               <ModalContent>
                  <ModalHeader>{_js('Unsaved Changes')}</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody border="1px solid lightgray" paddingBottom="20px">
                     <Text padding="15px">
                        {_js(
                           'You have unsaved changes. Are you sure you would like to exit without saving?'
                        )}
                     </Text>
                  </ModalBody>
                  <ModalFooter>
                     <Button
                        variant="outline"
                        colorScheme="gray"
                        marginRight="20px"
                        onClick={onClose}
                     >
                        {_js('Continue editing')}
                     </Button>
                     <Button
                        variant="outline"
                        colorScheme="red"
                        borderColor="#dc2626"
                        color="#dc2626"
                        onClick={() => {
                           setIsModified(false);
                           if (docUrl) {
                              router.push(docUrl);
                           }
                        }}
                     >
                        {_js('Discard changes and exit')}
                     </Button>
                  </ModalFooter>
               </ModalContent>
            </Modal>
            <MediaLibrary
               isOpen={isMediaLibraryOpen}
               onClose={onCloseMediaLibrary}
               _js={_js}
               filter="guide_main_image"
               onSelect={(ev: any) => {
                  const file = { webkitRelativePath: ev.standard };
                  openFileChooserOptions.callback([file]);
                  onCloseMediaLibrary();
               }}
            />
         </Stack>
      );
   } else {
      return (
         <Center height="100vh" width="100vw">
            <CircularProgress isIndeterminate />
         </Center>
      );
   }
};
